import moment from "moment-timezone"

import { API } from "."
import { makeUrlParams, makeParams } from "@/utils/helper"
import {
  CreateActivityPayload,
  CreateActivityResponse,
  DeleteActivityDataParams,
  ReadActivitiesAnalysisStatusParams,
  ReadActivitiesAnalysisStatusResponse,
  ReadActivityDataResponse,
  ReadActivityDetailResponse,
  ReadActivityListParams,
  ReadActivityListResponse,
  ReadActivityOverviewListParams,
  ReadActivityOverviewListResponse,
  ReadActivityParticipatedPlayerListParams,
  ReadActivityParticipatedPlayerListResponse,
  ReadActivityResponse,
  ReadActivityTypeStatsOfPlayerParams,
  ReadActivityTypeStatsOfPlayerResponse,
  ReadActivityTypeStatusResponse,
  ReadAnalyzingActivityResponse,
  ReadLatestActivityParams,
  ReadLatestActivityResponse,
  ReadPlayerRpeResponse,
  ReadRecentActivityOverviewParams,
  ReadRecentActivityOverviewResponse,
  ReadSessionListParams,
  ReadSessionListResponse,
  UpsertActivityPayload,
  UpsertActivityResponse,
  UpsertActivityTimePayload,
  UpsertPlayerRpePayload,
  UpsertPlayerRpeResponse,
  ReadActivitySearchResponse,
  ReadActivityPlayerDeviceListResponse,
} from "@/types/activity"

/**
 * @param {number|string|Array} idfTeam [Required]
 * @param {number|string|Date} start
 * @param {number|string|Date} endTime
 */
/* 액티비티 목록 조회 */
export async function readActivityList({ start, end, type, page, size }: ReadActivityListParams) {
  const params = makeParams({ start, end, type, page, size })

  return await API.get<ReadActivityListResponse>("/activities", { params })
}

/* 액티비티 생성 */
export async function createActivity(payload: CreateActivityPayload) {
  return await API.post<CreateActivityResponse>("/activities", payload)
}

/* 액티비티 상세 조회 */
export async function readActivity(activityId: number) {
  return await API.get<ReadActivityResponse>(`/activities/${activityId}`)
}

export async function readActivitySearchList(keyword: string) {
  const tz = moment.tz.guess()

  return await API.get<ReadActivitySearchResponse>(
    `/activities/search?name=${keyword}&timezone=${tz}`,
  )
}

/* 액티비티 수정 */
export async function upsertActivity(data: {
  idfActivity: number
  payload: UpsertActivityPayload
}) {
  const { idfActivity, payload } = data

  return await API.put<UpsertActivityResponse>(`/activities/${idfActivity}`, payload)
}

/* 액티비티 삭제 */
export async function deleteActivity(activityId: number) {
  return await API.delete<void>(`/activities/${activityId}`)
}

/**
 * @param {number} idfActivity [Required]
 * @param {Array} activityDataDtoList
 */
/* 액티비티 시간 수정 */
export async function upsertActivityTime(data: {
  idfActivity: number
  payload: UpsertActivityTimePayload
}) {
  const { idfActivity, payload } = data

  return await API.put<void>(`/activities/${idfActivity}/time`, payload)
}

/* 액티비티 개요 목록 조회 */
export async function readActivityOverviewList({
  start,
  end,
  type,
}: ReadActivityOverviewListParams) {
  const tz = moment.tz.guess()
  const params = makeParams({ start, end, type, timezone: tz })

  return await API.get<ReadActivityOverviewListResponse>("/activities/overview", {
    params,
  })
}

/* 최근 액티비티 개요 목록 조회  */
export async function readRecentActivityOverview({
  timezone,
  date,
  type,
  limit,
}: ReadRecentActivityOverviewParams) {
  const params = makeParams({ timezone, date, type, limit })
  params.timezone = moment.tz.guess()

  return await API.get<ReadRecentActivityOverviewResponse>("/activities/recent-overview", {
    params,
  })
}

/* 가장 최근 액티비티 조회  */
export async function readLatestActivity(params: ReadLatestActivityParams) {
  return await API.get<ReadLatestActivityResponse>("/activities/latest", { params })
}

/* 액티비티 데이터 조회  */
export async function readActivityData(idfActivity: number) {
  return await API.get<ReadActivityDataResponse>(`/activities/${idfActivity}/data`)
}

/* 액티비티 데이터 추가  */
export async function createActivityData(data: {
  idfActivity: number
  payload: CreateActivityPayload
}) {
  const { idfActivity, payload } = data

  return await API.post<CreateActivityResponse>(`/activities/${idfActivity}/data`, payload)
}

/* 액티비티 데이터 제외 */
export async function deleteActivityData(params: DeleteActivityDataParams) {
  const { idfActivity, idfData } = params
  const urlParams = makeUrlParams({ data: idfData })

  return await API.delete<void>(`/activities/${idfActivity}${urlParams}`)
}

/* 액티비티 타입 개수 조회  */
export async function readActivityTypeStats() {
  return await API.get<ReadActivityTypeStatusResponse>("/activities/type-stats")
}

/**
 * @param {number|string} idfPlayer [Required]
 * @param {number|string|Date} startTime
 * @param {number|string|Date} endTime
 */
export async function readActivityTypeStatsOfPlayer({
  idfPlayers,
  start,
  end,
}: ReadActivityTypeStatsOfPlayerParams) {
  const params = makeParams({ idfPlayers, start, end })

  return await API.get<ReadActivityTypeStatsOfPlayerResponse>(
    `/activities/players/${idfPlayers}/type-stats`,
    { params },
  )
}

/* 액티비티의 조회 시점의 분석 상태  */
export async function readAnalyzingActivity(idfActivity: number) {
  return await API.get<ReadAnalyzingActivityResponse>(`/activities/${idfActivity}/analysis-status`)
}

/* 조회 기간 내 모든 액티비티 목록을 분석 상태와 함께 조회  */
export async function readActivitiesAnalysisStatus(params: ReadActivitiesAnalysisStatusParams) {
  params.timezone = moment.tz.guess()

  return await API.get<ReadActivitiesAnalysisStatusResponse>(`/activities/analysis-status`, {
    params,
  })
}

/* 액티비티 분석  */
export async function createActivityAnalyze(idfActivity: number) {
  return await API.post<string>(`/activities/${idfActivity}/analyze`)
}

/* 액티비티 재분석? swagger 에는 존재하지 않음 */
export async function upsertReAnalyzeActivity(idfActivity: number) {
  return await API.put<void>(`/activities/${idfActivity}/re-analyze`)
}

/* 액티비티 플레이어 rpe 조회  */
export async function readPlayersRpe(activityId: number) {
  return await API.get<ReadPlayerRpeResponse>(`/activities/${activityId}/players/rpe`)
}

/* 액티비티 데이터 플레이어 설정 정보 변경  */
export async function upsertPlayersRpe(data: {
  activityId: number
  payload: UpsertPlayerRpePayload
}) {
  const { activityId, payload } = data

  return await API.put<UpsertPlayerRpeResponse>(`/activities/${activityId}/player-edits`, payload)
}

/* 액티비티에 참여한 선수 조회 */
export async function readActivityParticipatedPlayerList(
  params: ReadActivityParticipatedPlayerListParams,
) {
  const urlParams = makeUrlParams({ activities: params })

  return await API.get<ReadActivityParticipatedPlayerListResponse>(`${urlParams}/players`)
}

/**
 * @param {number|string} idfActivity [Required]
 * @param {number|string} idfPlayer
 */
/* 액티비티 세션 조회 */
export async function readSessionList({ idfActivity, idfPlayers }: ReadSessionListParams) {
  const urlParams = makeUrlParams({ activities: idfActivity, players: idfPlayers })

  return await API.get<ReadSessionListResponse>(`${urlParams}/sessions`)
}

/* 액티비티 상세 정보 조회 */
export async function readActivityDetail(idfActivity: number) {
  const params = makeParams({ timezone: moment.tz.guess() })

  return await API.get<ReadActivityDetailResponse>(`/activities/${idfActivity}/detail`, { params })
}

/** 필터 기반 경량 액티비티 목록 조회 (QuickReport 위젯용) — 분석 여부 무관, dateStart DESC */
export interface ReadFilteredActivitiesParams {
  start: number // timestamp
  end: number // timestamp
  type?: string // comma-separated (T,C,L,...)
  idfPlayer?: string // comma-separated (1,2,3)
  idfSeasonWeek?: string // comma-separated (5,7) 또는 -1(미지정)
  matchDay?: string // comma-separated (MD(H),MD-1,...)
}

export interface FilteredActivityItem {
  idfActivity: number
  name: string
  status: string // "ANALYZED" | "ANALYZING" | etc
  dateStart: string // ISO datetime
  dateFinish: string // ISO datetime
  type: string // ActivityTypeKey (T, C, L, F, R, E)
  sessionCount?: number
  playerCount?: number
}

export async function readFilteredActivities(params: ReadFilteredActivitiesParams) {
  const queryParams = makeParams(params as ReadFilteredActivitiesParams & Record<string, unknown>)

  return await API.get<FilteredActivityItem[]>("/activities/filtered", { params: queryParams })
}

export async function readActivityPlayerDeviceList(idfActivity?: number) {
  const url = idfActivity !== undefined ? `/activities/${idfActivity}` : "/activities"

  return await API.get<ReadActivityPlayerDeviceListResponse>(`${url}/player-with-device`)
}
